<script setup lang="ts">
import { computed, useId } from "vue";
import { useI18n } from "vue-i18n";
import { useVuelidate } from "@vuelidate/core";
import { helpers } from "@vuelidate/validators";
import { useValidators } from "@/composables/useValidators";
import {
  CNMC_RECIPIENT_FIELDS,
  cnmcRecipientChecks,
  type CnmcRecipientField,
} from "./cnmcRecipientRules";
import { exportDestinationDisplay } from "./cnmcPresentation";
import CnmcFieldErrors from "./CnmcFieldErrors.vue";
import SegmentedControl from "@/components/common/SegmentedControl.vue";
import InfoCallout from "@/components/common/InfoCallout.vue";
import type { CnmcRecipientDraft, CnmcRoute } from "./cnmcTypes";

/**
 * A CNMC recipient: a Spanish supplier's R2 code or a foreign EECS account, as the draft's route says.
 *
 * @property canSwitchRoute - Offers both routes (a PPA's recipient); otherwise the route is the caller's
 */
const props = withDefaults(
  defineProps<{
    destinations: { region: string; name: string }[];
    canSwitchRoute?: boolean;
    required?: boolean;
  }>(),
  { canSwitchRoute: false, required: true }
);
const model = defineModel<CnmcRecipientDraft>({ required: true });
const id = useId();
const { t } = useI18n();
const validators = useValidators(t);
const required = validators.requiredIf(
  () =>
    props.required ||
    Boolean(model.value.name || model.value.eecs || model.value.r2)
);
// Built from the same checks as isCnmcRecipientComplete, which gates Send's button.
const rules = computed(() => {
  const checks = cnmcRecipientChecks(model.value.route, props.destinations);
  const filed = CNMC_RECIPIENT_FIELDS[model.value.route];
  const fieldRules = (field: CnmcRecipientField) =>
    filed.includes(field)
      ? {
          required,
          ...Object.fromEntries(
            Object.entries(checks[field] ?? {}).map(([name, check]) => [
              name,
              helpers.withMessage(
                () => t(check.message, check.params ?? {}),
                (value: string) => !helpers.req(value) || check.test(value)
              ),
            ])
          ),
        }
      : {};
  return {
    name: fieldRules("name"),
    r2: fieldRules("r2"),
    eecs: fieldRules("eecs"),
  };
});
const destinationDisplay = computed(() =>
  model.value.route === "DOMESTIC"
    ? ""
    : exportDestinationDisplay(model.value.eecs, props.destinations, t)
);
const v$ = useVuelidate(rules, model, { $autoDirty: true });
const routeOptions = computed(() => [
  { value: "EXPORT" as const, label: t("cnmc.eecs_recipient") },
  { value: "DOMESTIC" as const, label: t("cnmc.domestic_recipient") },
]);
const setRoute = (route: CnmcRoute) => {
  model.value = { ...model.value, route, eecs: "", r2: "" };
};
const setField = (field: CnmcRecipientField, event: Event) => {
  model.value = {
    ...model.value,
    [field]: (event.target as HTMLInputElement).value,
  };
};
</script>

<template>
  <div class="row g-3">
    <div v-if="canSwitchRoute" class="col-12">
      <label class="form-label">{{ $t("cnmc.recipient_type") }}</label>
      <SegmentedControl
        :modelValue="model.route"
        :options="routeOptions"
        :aria-label="$t('cnmc.recipient_type')"
        @update:modelValue="setRoute"
      />
      <InfoCallout icon="file-lines" class="mt-3">
        {{
          $t(
            model.route === "DOMESTIC"
              ? "cnmc.domestic_delivery_help"
              : "cnmc.export_delivery_help"
          )
        }}
      </InfoCallout>
    </div>
    <div class="col-12">
      <label :for="`${id}-name`" class="form-label">{{
        $t("cnmc.recipient_name")
      }}</label>
      <input
        :id="`${id}-name`"
        :value="model.name"
        @input="setField('name', $event)"
        class="form-control"
        :class="{ 'is-invalid': v$.name.$error }"
        :aria-invalid="v$.name.$error"
        @blur="v$.name.$touch()"
      />
      <CnmcFieldErrors :validation="v$.name" />
    </div>
    <div v-if="model.route === 'DOMESTIC'" class="col-md-6">
      <label :for="`${id}-r2`" class="form-label">{{
        $t("cnmc.recipient_r2")
      }}</label>
      <input
        :id="`${id}-r2`"
        :value="model.r2"
        @input="setField('r2', $event)"
        class="form-control"
        :class="{ 'is-invalid': v$.r2.$error }"
        :aria-invalid="v$.r2.$error"
        @blur="v$.r2.$touch()"
        placeholder="R2-…"
      />
      <CnmcFieldErrors :validation="v$.r2" />
      <div class="form-text">{{ $t("cnmc.r2_help") }}</div>
    </div>
    <div v-else class="col-md-6">
      <label :for="`${id}-eecs`" class="form-label">{{
        $t("cnmc.eecs_account")
      }}</label>
      <div class="input-group has-validation">
        <input
          :id="`${id}-eecs`"
          :value="model.eecs"
          class="form-control text-uppercase"
          :class="{ 'is-invalid': v$.eecs.$error }"
          :aria-invalid="v$.eecs.$error"
          :aria-describedby="
            destinationDisplay ? `${id}-destination` : undefined
          "
          @blur="v$.eecs.$touch()"
          @input="setField('eecs', $event)"
        />
        <span
          v-if="destinationDisplay"
          :id="`${id}-destination`"
          class="input-group-text bg-light text-muted small"
          >{{ destinationDisplay }}</span
        >
        <CnmcFieldErrors :validation="v$.eecs" />
      </div>
    </div>
  </div>
</template>
